/**
 * Chinese (Simplified) dictionary — canonical source for the ops-console
 * key space.
 *
 * `en.ts` re-uses this dictionary's type (`typeof zhCN`) so every key
 * that exists here must exist there. Adding a new string: add the key
 * to this file first, then add the English translation. Removing a
 * string: delete from both files and also sweep call sites for the key.
 *
 * Conventions
 * - Keys are lowercase dot-paths: `feature.subfeature.detail`.
 * - Templates use `{name}` placeholders; the `t()` formatter substitutes
 *   on render. Unknown placeholders render literally so missing params
 *   fail loud in QA rather than silently eat text.
 * - Punctuation: Chinese sentences end in `。`, lists use `、`; follow
 *   the surrounding copy for spacing. English sentences follow a normal
 *   period. Do not include trailing whitespace.
 * - Identifiers that surface verbatim in the UI (vendor ids, SQL table
 *   names, API path fragments, code-block literals) stay in the JSX
 *   rather than living in the dictionary — they are not translatable
 *   content and would drift if mirrored.
 */
export const zhCN = {
  // ── <head> metadata ─────────────────────────────────────────────
  // Synced to `document.title` and `<meta name="description">` at
  // runtime by `I18nProvider`. The static value in `index.html` is the
  // zh-CN first-paint fallback; an en-preferring visitor's provider
  // effect rewrites both on mount.
  'meta.title': '哪都通运维控制台',
  'meta.description':
    '哪都通运维控制台:上游管理、模型定价、礼品卡发放、账户与审计、收入与支付对账。',

  // ── Common / shared ─────────────────────────────────────────────
  'common.retry': '重试',
  'common.cancel': '取消',
  'common.close': '关闭',
  'common.save': '保存',
  'common.saving': '保存中…',
  'common.delete': '删除',
  'common.deleting': '删除中…',
  'common.loading': '加载中…',
  'common.network_error': '网络错误',
  'common.previous': '上一页',
  'common.next': '下一页',
  'common.never': '从未',
  'common.copied': '已复制到剪贴板',
  'common.click_to_copy': '点击复制',
  'common.copy_anywhere': '点击此区块任意位置复制',

  // Locale-switcher native labels live in `types.ts` as
  // `LOCALE_NATIVE_LABELS` — they are UI chrome (the name of each
  // language in its own script) rather than translatable content.
  'i18n.switcher.aria_label': '语言',

  // ── Auth guard ──────────────────────────────────────────────────
  'auth.checking': '正在检查会话…',

  // ── Login ───────────────────────────────────────────────────────
  'login.brand': 'Ops Console',
  'login.subtitle': '运维登录',
  'login.email': '邮箱',
  'login.password': '密码',
  'login.submit': '登录',
  'login.submitting': '登录中…',
  'login.error.empty_fields': '请填写邮箱和密码',

  // ── Shell chrome ────────────────────────────────────────────────
  'shell.brand.title': 'Ops Console',
  'shell.brand.subtitle': '哪都通 · α-7 工作站',
  'shell.nav.aria_label': '主导航',
  'shell.group.overview': '总览',
  'shell.group.customers': '客户运营',
  'shell.group.platform': '平台',
  'shell.group.finance': '财务',
  'shell.group.support': '支持',
  'shell.group.compliance': '合规',
  'shell.group.system': '系统',
  'shell.group.supply': '供应与路由',
  'shell.group.governance': '治理与系统',
  'shell.nav.overview': '业务总览',
  'shell.nav.route_health': '路由健康',
  'shell.nav.customer_usage': '用量总览',
  'shell.nav.request_logs': '请求日志',
  'shell.nav.dashboard': '仪表盘',
  'shell.nav.upstreams': '上游',
  'shell.nav.models': '模型',
  'shell.nav.gift_cards': '礼品卡',
  'shell.nav.accounts': '账户',
  'shell.nav.failed_deductions': '扣款失败',
  'shell.nav.billing_anomalies': '账务异常',
  'shell.nav.payments_orders': '支付订单',
  'shell.nav.payment_providers': '支付通道',
  'shell.nav.pricing': '汇率',
  'shell.nav.fx_policy': '汇率策略',
  'shell.nav.channels': '渠道',
  'shell.nav.revenue': '收入',
  'shell.nav.reports': '报表',
  'shell.nav.webhook_events': 'Webhook 事件',
  'shell.nav.cron_runs': '定时任务运行',
  'shell.nav.ops_users': '运维账户',
  'shell.nav.dr_check': '灾备演练',
  'shell.nav.support_requests': '支持请求',
  'shell.nav.audit': '审计',
  'shell.system_status': '系统状态 · 在线',
  'shell.signed_in_fallback': '已登录',
  'shell.role.fallback': 'OPS-ADMIN',
  'shell.logout': '退出',
  'shell.header.search.placeholder': '搜索记录…',
  'shell.header.search.aria_label': '搜索',
  'shell.header.notifications.aria_label': '通知',
  'shell.page.console_fallback': '运维控制台',

  // ── Not found ───────────────────────────────────────────────────
  'notfound.title': '页面未找到',
  'notfound.body_prefix': '你访问的路由暂未定义。',
  'notfound.back': '返回仪表盘',

  // ── Dashboard (Mission Control) ─────────────────────────────────
  'dashboard.title': '任务控制台',
  'dashboard.subtitle': '按上游分组的路由流量',
  'dashboard.window.label': '时间窗口',
  'dashboard.window.7d': '最近 7 天',
  'dashboard.window.30d': '最近 30 天',
  'dashboard.window.90d': '最近 90 天',
  'dashboard.stat.upstreams.label': '活跃上游',
  'dashboard.stat.upstreams.delta': '最近 {days} 天内出现',
  'dashboard.stat.requests.label': '总请求数',
  'dashboard.stat.requests.delta': '{days} 天窗口累计',
  'dashboard.stat.failure_rate.label': '失败率',
  'dashboard.stat.failure_rate.delta': 'HTTP 4xx/5xx 占比',
  'dashboard.stat.cost.label': '开销',
  'dashboard.stat.cost.delta': '网关累计费用',
  'dashboard.loading_aria': '正在加载路由统计…',
  'dashboard.error.title': '无法加载路由统计',
  'dashboard.empty.title': '当前窗口暂无流量',
  'dashboard.empty.body_prefix': '最近 {days} 天 ',
  'dashboard.empty.body_suffix': ' 中无记录。请放宽窗口或确认网关正在接收请求。',
  'dashboard.column.upstream': '上游',
  'dashboard.column.requests': '请求数',
  'dashboard.column.failure_rate': '失败率',
  'dashboard.column.latency': 'p95(均值)',
  'dashboard.column.cost': '费用',
  'dashboard.row.deleted_upstream': '已删除上游',
  'dashboard.latency_note': '延迟列目前为网关中 p95 的 AVG 近似(参见 router-stats 契约)。',

  // ── Dashboard proactive alerts (Epic #109 / P0.3) ───────────────
  'dashboard.alerts.title': '当前告警',
  'dashboard.alerts.subtitle': '需要运维关注的关键指标',
  'dashboard.alerts.loading': '正在加载告警…',
  'dashboard.alerts.error.title': '无法加载告警',
  'dashboard.alerts.all_clear': '所有指标正常,当前无需处理的告警。',
  'dashboard.alerts.unrouted.title': '无上游 SKU',
  'dashboard.alerts.unrouted.description':
    '有定价但没有启用上游支持的 (vendor, model, protocol) 三元组。租户调用会失败。',
  'dashboard.alerts.unrouted.count': '{count} 个 SKU',
  'dashboard.alerts.single_point.title': '单点 SKU',
  'dashboard.alerts.single_point.description':
    '仅有一个启用上游支持的 SKU。上游故障会导致服务中断,建议再挂一个上游。',
  'dashboard.alerts.single_point.count': '{count} 个 SKU',
  'dashboard.alerts.low_funding.title': '额度告急上游',
  'dashboard.alerts.low_funding.description':
    '非已耗尽的批次剩余额度低于 20%。继续发流量会很快变为耗尽状态。',
  'dashboard.alerts.low_funding.count': '{count} 个上游',
  'dashboard.alerts.low_funding.row': '{name} · 剩余 {pct} ({remaining} / {capacity})',
  'dashboard.alerts.low_funding.row_unknown': '{id} · 剩余 {pct} ({remaining} / {capacity})',
  'dashboard.alerts.unhealthy.title': '24 小时异常上游',
  'dashboard.alerts.unhealthy.description':
    '最近 24 小时非探针流量 ≥ 20 次且 5xx 失败率超过 20% 的上游。',
  'dashboard.alerts.unhealthy.count': '{count} 个上游',
  'dashboard.alerts.unhealthy.row': '{name} · 失败率 {rate} ({failures} / {requests})',
  'dashboard.alerts.unhealthy.row_unknown': '{id} · 失败率 {rate} ({failures} / {requests})',

  // ── Upstreams list ──────────────────────────────────────────────
  'upstreams.title': '上游',
  'upstreams.subtitle': '管理从网关路由出去的 forward + pool + BYOK 供应源。',
  'upstreams.search.placeholder': '搜索名称、端点…',
  'upstreams.search.aria_label': '搜索上游',
  'upstreams.new': '新建上游',
  'upstreams.new.title': '启动 4 步上游接入向导:基础信息 → 挂模型 → 登记资金 → 探针自检',
  'upstreams.error.title': '无法加载上游',
  'upstreams.loading_aria': '正在加载上游…',
  'upstreams.empty.title': '暂无已配置的上游',
  'upstreams.empty.search_body': '没有匹配此搜索的上游。清空筛选或创建新条目。',
  'upstreams.empty.initial_body':
    '通过 admin bootstrap 流程(网关 /internal/upstreams)创建第一个上游,方可开始路由流量。',
  'upstreams.stats.total': '总上游',
  'upstreams.stats.enabled': '已启用',
  'upstreams.stats.accounts': '账户总数',
  'upstreams.stats.funding': '活跃资金',
  'upstreams.column.name': '名称',
  'upstreams.column.endpoint': '端点',
  'upstreams.column.source': '来源码',
  'upstreams.column.status': '状态',
  'upstreams.column.accounts': '账户',
  'upstreams.column.mappings': '映射',
  'upstreams.column.funding': '资金',
  'upstreams.column.priority': '优先级',
  'upstreams.column.actions': '操作',
  'upstreams.status.enabled': 'enabled',
  'upstreams.status.disabled': 'disabled',
  'upstreams.row.copy_endpoint': '复制端点 URL',
  'upstreams.row.probe': '探针',
  'upstreams.row.edit': '编辑',
  'upstreams.row.delete': '删除',
  'upstreams.delete_modal.title': '软删除上游?',
  'upstreams.delete_modal.body_suffix':
    ' 将被标记为已删除(网关停止向其路由)。需通过后续迁移修改 `deleted_at` 才可恢复。',

  // ── Upstream detail ─────────────────────────────────────────────
  'upstream_detail.error.title': '无法加载上游',
  'upstream_detail.back': '返回上游列表',
  'upstream_detail.breadcrumb.upstreams': '上游',
  'upstream_detail.meta.kind': '类型 · {kind}',
  'upstream_detail.meta.visibility': '可见性 · {visibility}',
  'upstream_detail.chip.enabled': '已启用',
  'upstream_detail.chip.disabled': '已禁用',
  'upstream_detail.config.title': '配置',
  'upstream_detail.config.display_name': '显示名称',
  'upstream_detail.config.endpoint_url': '端点 URL',
  'upstream_detail.config.priority': '优先级',
  'upstream_detail.config.enabled': '启用',
  'upstream_detail.config.auth_secret': '已存储密钥',
  'upstream_detail.config.auth_secret.write_only': '密钥已写入(只写不可回显)',
  'upstream_detail.config.auth_secret.empty': '尚未写入密钥',
  'upstream_detail.config.auth_secret.hint':
    '网关会将此密钥加密存储。运维可轮换，但不能从控制台读回原值。',
  'upstream_detail.config.cookie_passthrough': 'Cookie 透传',
  'upstream_detail.config.cookie_passthrough.state.enabled': '该上游会收到租户原始 Cookie。',
  'upstream_detail.config.cookie_passthrough.state.disabled': '网关会在出站前剥离租户 Cookie。',
  'upstream_detail.config.cookie_passthrough.hint':
    '除非上游明确依赖租户 Cookie 维持鉴权或会话，否则保持关闭。',
  'upstream_detail.config.state.enabled': '上游处于启用状态',
  'upstream_detail.config.state.disabled': '上游处于禁用状态',
  'upstream_detail.probe.title': '探针',
  'upstream_detail.probe.hint': '结果仅在本次会话内保留',
  'upstream_detail.probe.vendor': '厂商',
  'upstream_detail.probe.vendor_placeholder': '选择厂商',
  'upstream_detail.probe.channel': '渠道',
  'upstream_detail.probe.channel_placeholder': '选择渠道',
  'upstream_detail.probe.model': '模型',
  'upstream_detail.probe.select.placeholder': '选择模型',
  'upstream_detail.probe.submit': '探测 {name}',
  'upstream_detail.probe.submitting': '探测中…',
  'upstream_detail.probe.error.select_model': '请先选择厂商、渠道和模型',
  'upstream_detail.probe_history.title': '探测历史',
  'upstream_detail.probe_history.body': '当前上游的已持久化探测结果。新探测完成后自动刷新。',
  'upstream_detail.probe_history.count': '最近 {count} 条',
  'upstream_detail.probe_history.loading': '正在加载探测历史…',
  'upstream_detail.probe_history.error': '加载探测历史失败。',
  'upstream_detail.probe_history.empty': '暂无探测记录。',
  'upstream_detail.probe_history.column.time': '时间 (UTC)',
  'upstream_detail.probe_history.column.model': '厂商 / 模型',
  'upstream_detail.probe_history.column.outcome': '结果',
  'upstream_detail.probe_history.column.latency': '延迟',
  'upstream_detail.probe_history.column.cost': '成本 (USD)',
  'upstream_detail.probe_history.column.status': 'HTTP',
  'upstream_detail.routing.title': '能力探测与路由绑定',
  'upstream_detail.routing.subtitle':
    '先确认上游真实可用哪些模型，再把通过探测的能力绑定成租户可用路由。',
  'upstream_detail.routing.protocol': '探测协议',
  'upstream_detail.routing.discover': '拉取模型清单',
  'upstream_detail.routing.discovering': '正在拉取模型…',
  'upstream_detail.routing.toast.models_loaded': '已拉取模型清单：{count} 个候选',
  'upstream_detail.routing.toast.capability_probe_queued':
    '已创建单模型 CLI 探测任务，正在等待结果',
  'upstream_detail.routing.toast.capability_ignored': '已忽略该模型候选',
  'upstream_detail.routing.toast.capability_unignored': '已取消忽略，请重新探测后再绑定',
  'upstream_detail.routing.capabilities.title': '上游实测能力',
  'upstream_detail.routing.capabilities.subtitle':
    '这张表不是租户路由；它展示所选渠道和厂商下从 /v1/models 发现的上游候选。可以逐个模型发起 CLI smoke probe，只有已通过并创建路由绑定后才会进入热路径。',
  'upstream_detail.routing.capabilities.empty': '暂无候选。先拉取模型清单。',
  'upstream_detail.routing.capabilities.empty_channel': '当前渠道暂无探测结果。',
  'upstream_detail.routing.capabilities.empty_routable':
    '当前渠道暂无可绑定候选；可切换到全部记录查看失败或待探测项。',
  'upstream_detail.routing.capabilities.empty_filter': '当前筛选下暂无记录。',
  'upstream_detail.routing.capabilities.filter.channel': '能力渠道',
  'upstream_detail.routing.capabilities.filter.channel.empty': '暂无渠道',
  'upstream_detail.routing.capabilities.filter.vendor': '厂商',
  'upstream_detail.routing.capabilities.filter.vendor.empty': '暂无厂商',
  'upstream_detail.routing.capabilities.filter.mode': '结果',
  'upstream_detail.routing.capabilities.filter.mode.routable': '可绑定',
  'upstream_detail.routing.capabilities.filter.mode.all': '全部记录',
  'upstream_detail.routing.capabilities.summary.total': '候选 {count}',
  'upstream_detail.routing.capabilities.summary.bindable': '可绑定 {count}',
  'upstream_detail.routing.capabilities.summary.bound': '已绑定 {count}',
  'upstream_detail.routing.capabilities.summary.failed': '失败 {count}',
  'upstream_detail.routing.capabilities.summary.needs_probe': '待探测 {count}',
  'upstream_detail.routing.capabilities.summary.ignored': '已忽略 {count}',
  'upstream_detail.routing.capabilities.group.count': '{count} 条探测记录',
  'upstream_detail.routing.capabilities.col.channel': '渠道',
  'upstream_detail.routing.capabilities.col.model': '上游候选模型',
  'upstream_detail.routing.capabilities.col.status': 'CLI 探测状态',
  'upstream_detail.routing.capabilities.col.action': '路由操作',
  'upstream_detail.routing.capability.status.passed': '已通过',
  'upstream_detail.routing.capability.status.passed.ready': '可以绑定成新路由。',
  'upstream_detail.routing.capability.status.passed.hint': 'CLI 已通过；最近通过：{time}',
  'upstream_detail.routing.capability.status.needs_probe': '待探测',
  'upstream_detail.routing.capability.status.needs_probe.hint': '需要先完成一次成功探测。',
  'upstream_detail.routing.capability.status.probe_failed': '探测失败',
  'upstream_detail.routing.capability.status.probe_failed.hint': '请检查 key、渠道绑定或模型名。',
  'upstream_detail.routing.capability.status.missing_from_list': '列表缺失',
  'upstream_detail.routing.capability.status.missing_from_list.hint':
    '最新上游模型列表里没有看到这个模型。',
  'upstream_detail.routing.capability.status.mismatch': '不匹配',
  'upstream_detail.routing.capability.status.mismatch.hint':
    '当前探测状态和既有能力记录不一致，需要人工复核。',
  'upstream_detail.routing.capability.status.ignored': '已忽略',
  'upstream_detail.routing.capability.status.ignored.hint':
    '此候选已被人工隐藏，不进入默认候选清单；取消忽略后需要重新探测。',
  'upstream_detail.routing.capability.action.bound': '已创建绑定',
  'upstream_detail.routing.capability.action.bind': '创建路由绑定',
  'upstream_detail.routing.capability.action.needs_pass': '探测未通过',
  'upstream_detail.routing.capability.action.ignored': '已忽略',
  'upstream_detail.routing.capability.action.ignore': '忽略',
  'upstream_detail.routing.capability.action.unignore': '取消忽略',
  'upstream_detail.routing.capability.action.probe': '发起探测',
  'upstream_detail.routing.capability.action.reprobe': '重新探测',
  'upstream_detail.routing.capability.action.probing': '创建中…',
  'upstream_detail.routing.capability.action.waiting': '等待结果…',
  'upstream_detail.routing.bindings.title': '租户路由绑定',
  'upstream_detail.routing.bindings.subtitle':
    '这里才是租户请求会使用的候选路由；只有启用状态会进入热路径，未启用只保留配置。',
  'upstream_detail.routing.bindings.empty': '暂无路由绑定。先从上方已通过能力创建绑定。',
  'upstream_detail.routing.bindings.col.channel': '渠道',
  'upstream_detail.routing.bindings.col.model': '模型',
  'upstream_detail.routing.bindings.col.enabled': '热路径',
  'upstream_detail.routing.bindings.col.action': '操作',
  'upstream_detail.routing.bindings.enabled.yes': '启用',
  'upstream_detail.routing.bindings.enabled.no': '停用',
  'upstream_detail.routing.bindings.action.enable': '启用',
  'upstream_detail.routing.bindings.action.disable': '停用',
  'upstream_detail.routing.bindings.action.delete': '删除',
  'upstream_detail.routing.legacy_notice':
    '下方旧映射只保留迁移可见性，不再作为新路由配置入口；新的租户路由以上方探测能力和路由绑定为准。',
  'upstream_detail.funding.title': '资金批次',
  'upstream_detail.funding.new': '+ 新建批次',
  'upstream_detail.funding.loading_aria': '正在加载资金批次…',
  'upstream_detail.funding.error.load': '无法加载资金批次',
  'upstream_detail.funding.empty': '尚未登记资金批次。按需新增以记录当前余额或预付额度。',
  'upstream_detail.funding.account.unassigned': '未关联账户',
  'upstream_detail.funding.form.account.label': '关联账户',
  'upstream_detail.funding.form.account.none': '无账户',
  'upstream_detail.funding.register.title': '登记资金批次',
  'upstream_detail.funding.register.submit': '登记批次',
  'upstream_detail.funding.register.submitting': '保存中…',
  'upstream_detail.funding.batch.mode_rate': '模式 · {mode} · 汇率 {rate}',
  'upstream_detail.funding.batch.chip.committed': '已入库',
  'upstream_detail.funding.batch.chip.draft': '未入库',
  'upstream_detail.funding.batch.chip.active': '活跃',
  'upstream_detail.funding.batch.chip.depleted': '已耗尽',
  'upstream_detail.funding.batch.chip.alert_95': '警告 95%',
  'upstream_detail.funding.batch.chip.alert_80': '警告 80%',
  'upstream_detail.funding.batch.meta.paid': '已付',
  'upstream_detail.funding.batch.meta.capacity': '额度',
  'upstream_detail.funding.batch.meta.consumed': '已消耗',
  'upstream_detail.funding.batch.meta.funded_at': '入款时间',
  'upstream_detail.funding.batch.pct_consumed': '{pct}% 已消耗',
  'upstream_detail.funding.batch.invoice': '发票',
  'upstream_detail.funding.batch.note': '备注',
  'upstream_detail.funding.form.cny_paid_yuan.label': '已付人民币（元）',
  'upstream_detail.funding.form.exchange_rate.label': '汇率（CNY/USD）',
  'upstream_detail.funding.form.exchange_rate.loading': '正在获取汇率…',
  'upstream_detail.funding.form.computed_usd.label': '折算 USD 额度',
  'upstream_detail.funding.form.invoice_ref.label': '上游发票号（可选）',
  'upstream_detail.funding.form.note.label': '备注（可选）',
  'upstream_detail.funding.error.cny_paid_required': '请输入已付人民币金额',
  'upstream_detail.funding.error.account_required': '请选择一个上游账户',
  'upstream_detail.funding.error.rate_unavailable': '汇率不可用，请先在汇率页设定',
  'upstream_detail.funding.toast.registered': '资金批次 {id} 已登记',
  'upstream_detail.funding.toast.committed': '资金批次已入库',
  'upstream_detail.funding.toast.updated': '资金批次已更新',
  'upstream_detail.funding.batch.action.edit': '编辑',
  'upstream_detail.funding.batch.action.commit': '入库',
  'upstream_detail.danger.title': '危险操作',
  'upstream_detail.danger.body':
    '软删除此上游。网关将停止向其路由,但模型映射保留。可在后续迁移中恢复。',
  'upstream_detail.danger.already_deleted': '已软删除',
  'upstream_detail.danger.delete': '软删除上游',
  'upstream_detail.delete_modal.title': '软删除 {name}?',
  'upstream_detail.delete_modal.body': '网关会立即停止向此上游路由。模型映射保留在原位。',

  // ── Upstream retirement impact dialog ───────────────────────────
  // Rendered by DangerZone before soft-deleting an upstream (Epic
  // #109 / P0.1). Tells the operator what will break — uniquely
  // served SKUs, active funding batches, last-24h traffic share —
  // and gates the Delete button behind an explicit acknowledgement.
  'upstream_detail.retirement.loading': '分析退役影响中…',
  'upstream_detail.retirement.load_error': '无法加载退役影响。请重试,或联系平台管理员。',
  'upstream_detail.retirement.retry': '重试',
  'upstream_detail.retirement.uniquely_served.title': '🚨 唯一承接的模型',
  'upstream_detail.retirement.uniquely_served.body':
    '退役后以下 {count} 个模型将没有任何上游可路由,tenant 调用会立刻返回 404:',
  'upstream_detail.retirement.uniquely_served.none':
    '无唯一承接。所有模型路由映射都有其他上游可路由。',
  'upstream_detail.retirement.total_attached_suffix':
    '(共映射 {total} 个模型路由,其中 {unique} 个仅此上游承接)',
  'upstream_detail.retirement.funding.title': '💰 活跃资金批次',
  'upstream_detail.retirement.funding.body':
    '{count} 批尚未耗尽,剩余额度 {remaining}。退役后这些批次不再被消耗,审计追溯中断。',
  'upstream_detail.retirement.funding.none': '无活跃资金批次。',
  'upstream_detail.retirement.traffic.title': '📊 近 24 小时流量份额',
  'upstream_detail.retirement.traffic.body':
    '此上游承接了近 24 小时网关流量的 {percentage}%({handled}/{total} 次请求)。',
  'upstream_detail.retirement.traffic.no_traffic': '近 24 小时无流量。流量份额无法估算。',
  'upstream_detail.retirement.ack': '我已了解退役此上游的影响,并希望继续软删除。',
  'upstream_detail.retirement.ack_required': '请勾选上方确认框后继续。',

  // ── Models list ─────────────────────────────────────────────────
  'models.title': '模型',
  'models.subtitle': '已注册的 (厂商, 模型) 目录。定价历史与协议绑定在单模型详情页管理。',
  'models.search.placeholder': '搜索模型、显示名称…',
  'models.search.aria_label': '搜索模型',
  'models.filter.vendor': '厂商',
  'models.filter.vendor_all': '全部厂商',
  'models.new': '发现模型',
  'models.error.title': '无法加载模型',
  'models.loading_aria': '正在加载模型…',
  'models.empty.search.title': '没有匹配此搜索的模型',
  'models.empty.search.body': '清空筛选，或先到厂商页发现模型。',
  'models.empty.initial.title': '尚未发现任何模型',
  'models.empty.initial.body': '先到厂商页执行官方发现，发现结果会以「下架」候选进入这里。',
  'models.column.vendor': '厂商',
  'models.column.model': '模型',
  'models.column.display_name': '显示名称',
  'models.column.price': '价格',
  'models.column.family': '家族',
  'models.column.context_window': '上下文窗口',
  'models.column.actions': '操作',
  'models.price.unit': '输入 / 输出 · USD/MTok',
  'models.price.cache_line': '缓存读 {read} / 写 {write}',
  'models.price.unpriced': '未定价',
  'models.row.empty_value': '—',
  'models.row.manage': '管理',
  'models.row.delete': '删除',
  'models.delete_modal.title': '软删除模型?',
  'models.delete_modal.body_suffix':
    ' 将被标记为已删除。定价和协议行会保留以便于历史重建;如需恢复,请后续通过迁移脚本修改 ',
  'models.delete_modal.body_deleted_at_code': 'deleted_at',
  'models.delete_modal.body_tail': ' 字段。',

  // ── Model detail ────────────────────────────────────────────────
  'model_detail.loading_aria': '正在加载模型…',
  'model_detail.error.title': '无法加载模型',
  'model_detail.back': '返回模型列表',
  'model_detail.breadcrumb.models': '模型',
  'model_detail.meta.family': '家族 · {family}',
  'model_detail.meta.soft_deleted': '已软删除 · {date}',
  'model_detail.meta.deprecated': '已弃用 · {date}',
  'model_detail.basic.title': '基本信息',
  'model_detail.basic.display_name': '显示名称',
  'model_detail.basic.family': '家族',
  'model_detail.basic.context_window': '上下文窗口',
  'model_detail.basic.max_output_tokens': '最大输出 tokens',
  'model_detail.pricing.title': '定价历史',
  'model_detail.pricing.loading_aria': '正在加载定价…',
  'model_detail.pricing.error.load': '加载定价失败',
  'model_detail.pricing.empty': '尚无定价行,请在下方新增第一代。',
  'model_detail.pricing.column.effective_from': '生效时间',
  'model_detail.pricing.column.input': '输入 $/Mtok',
  'model_detail.pricing.column.output': '输出 $/Mtok',
  'model_detail.pricing.column.cache_read': '缓存读 $/Mtok',
  'model_detail.pricing.column.cache_write': '缓存写 $/Mtok',
  'model_detail.pricing.column.status': '状态',
  'model_detail.pricing.row.current': '当前',
  'model_detail.pricing.row.archived': '已归档',
  'model_detail.pricing.add.title': '新增定价行',
  'model_detail.pricing.add.effective_from.label': '生效时间(日期)',
  'model_detail.pricing.add.input_rate.label': '输入 $/Mtok',
  'model_detail.pricing.add.input_rate.placeholder': '3.000000',
  'model_detail.pricing.add.output_rate.label': '输出 $/Mtok',
  'model_detail.pricing.add.output_rate.placeholder': '15.000000',
  'model_detail.pricing.add.cache_read.label': '缓存读 $/Mtok',
  'model_detail.pricing.add.cache_read.placeholder': '0.300000',
  'model_detail.pricing.add.cache_write.label': '缓存写 $/Mtok',
  'model_detail.pricing.add.cache_write.placeholder': '3.750000',
  'model_detail.pricing.add.submit': '新增定价行',
  'model_detail.pricing.add.submitting': '新增中…',
  'model_detail.pricing.error.effective_from_required': '生效时间不能为空(ISO-8601 UTC)',
  'model_detail.pricing.error.effective_from_invalid': '生效时间必须是有效日期',
  'model_detail.pricing.error.rates_required': '输入与输出费率必须填写',
  'model_detail.pricing.error.input_rate_invalid': '输入费率必须为非负数',
  'model_detail.pricing.error.output_rate_invalid': '输出费率必须为非负数',
  'model_detail.pricing.error.cache_read_invalid': '缓存读费率必须为非负数',
  'model_detail.pricing.error.cache_write_invalid': '缓存写费率必须为非负数',
  'model_detail.pricing.error.invalid': '费率无效',
  'model_detail.protocols.title': '协议',
  'model_detail.protocols.loading_aria': '正在加载协议…',
  'model_detail.protocols.error.load': '加载协议失败',
  'model_detail.protocols.empty': '尚未登记协议。Pool 选择器会跳过此模型。',
  'model_detail.protocols.add.label': '新增协议',
  'model_detail.protocols.add.submit': '新增',
  'model_detail.protocols.add.adding': '新增中…',
  'model_detail.protocols.error.already_registered': '{protocol} 已登记',
  'model_detail.protocols.remove_aria': '移除 {protocol}',
  'model_detail.deprecation.title': '弃用状态',
  'model_detail.deprecation.body':
    '标记此模型为弃用后,仍可列表 / 计费 / 路由;目的仅为提示新用户使用替代模型。弃用理由会出现在运营面板的模型详情页顶部,并通过审计日志追溯。',
  'model_detail.deprecation.chip.deprecated': '已弃用',
  'model_detail.deprecation.notice.label': '弃用理由(必填)',
  'model_detail.deprecation.notice.placeholder':
    '例如:厂商于 2026-06-01 起停止此模型,请迁移至 claude-opus-4-8。',
  'model_detail.deprecation.notice.counter': '{count} / {limit}',
  'model_detail.deprecation.deprecate.submit': '标记为弃用',
  'model_detail.deprecation.deprecate.pending': '标记中…',
  'model_detail.deprecation.undeprecate.submit': '恢复为可用',
  'model_detail.deprecation.undeprecate.pending': '恢复中…',
  'model_detail.deprecation.error.notice_required': '请填写弃用理由',
  'model_detail.deprecation.error.notice_too_long': '弃用理由不得超过 {limit} 字符',
  'model_detail.deprecation.toast.deprecated': '模型已标记为弃用',
  'model_detail.deprecation.toast.undeprecated': '模型弃用状态已清除',
  'model_detail.danger.title': '危险操作',
  'model_detail.danger.body':
    '软删除此模型。定价行与协议绑定会保留在 D1 中以供历史成本重建;网关 pool 选择器会停止提供该模型。',
  'model_detail.danger.already_deleted': '已软删除',
  'model_detail.danger.delete': '软删除模型',
  'model_detail.delete_modal.title': '软删除 {vendor}/{model}?',
  'model_detail.delete_modal.body':
    '网关 pool 选择器会立即停止提供该模型。历史用量日志仍通过保留的定价行解析。',

  // ── Gift cards ──────────────────────────────────────────────────
  'gift_cards.title': '礼品卡',
  'gift_cards.subtitle':
    '按批次发行、查询和追踪兑换状态；租户自助兑换与 ops 代兑都会回到同一张清单。',
  'gift_cards.batch.unbatched': '未分批',
  'gift_cards.batches.title': '批次',
  'gift_cards.batches.empty': '暂无批次',
  'gift_cards.batches.status.active': '启用',
  'gift_cards.batches.status.closed': '已关闭',
  'gift_cards.batches.close': '关闭',
  'gift_cards.batches.reopen': '恢复',
  'gift_cards.batches.metric.issued': '已发行',
  'gift_cards.batches.metric.active': '可用',
  'gift_cards.batches.metric.redeemed': '已兑',
  'gift_cards.batches.metric.value': '总面值',
  'gift_cards.batches.create.name': '批次名称',
  'gift_cards.batches.create.description': '描述(可选)',
  'gift_cards.batches.create.submit': '创建批次',
  'gift_cards.batches.create.submitting': '创建中…',
  'gift_cards.batches.error.name_required': '批次名称不能为空',
  'gift_cards.chip.revoked': '已吊销',
  'gift_cards.chip.redeemed': '已兑换',
  'gift_cards.chip.expired': '已过期',
  'gift_cards.chip.active': '可用',
  'gift_cards.issue.title': '发行',
  'gift_cards.issue.subtitle': '核心路径 · 创建一张余额凭证',
  'gift_cards.issue.amount.label': '金额(RMB)',
  'gift_cards.issue.amount.placeholder': '50',
  'gift_cards.issue.code_hint.label': '码前缀(可选)',
  'gift_cards.issue.code_hint.placeholder': '自动生成 32 位十六进制',
  'gift_cards.issue.batch_id.label': '批次 ID(可选)',
  'gift_cards.issue.batch_id.placeholder': '2026-q2-promo',
  'gift_cards.issue.expires_at.label': '过期时间(可选)',
  'gift_cards.issue.submit': '发行礼品卡',
  'gift_cards.issue.submitting': '发行中…',
  'gift_cards.issued.title': '已发行新礼品卡',
  'gift_cards.issued.code_label': '兑换码(点击复制)',
  'gift_cards.issued.code_aria_label': '复制礼品卡兑换码',
  'gift_cards.issued.copy_status.copied': '已复制到剪贴板',
  'gift_cards.issued.copy_status.idle': '点击整块以复制',
  'gift_cards.issued.face_value': '面值',
  'gift_cards.issued.batch': '批次',
  'gift_cards.issued.expires_at': '过期时间',
  'gift_cards.issued.never': '永不过期',
  'gift_cards.issued.credit_value': '入账金额(USD)',
  'gift_cards.issued.applied_rate': '锁定汇率',
  'gift_cards.issued.issue_another': '再发一张',
  'gift_cards.lookup.title': '查询',
  'gift_cards.lookup.clear': '清空',
  'gift_cards.lookup.code.label': '礼品卡兑换码',
  'gift_cards.lookup.code.placeholder': '32 位十六进制兑换码',
  'gift_cards.lookup.submit': '查询',
  'gift_cards.lookup.submitting': '查询中…',
  'gift_cards.lookup.error.network': '网络错误',
  'gift_cards.list.title': '清单',
  'gift_cards.list.batch.all': '全部批次',
  'gift_cards.list.status.all': '全部状态',
  'gift_cards.list.account.placeholder': '兑换账户',
  'gift_cards.list.column.code': '兑换码',
  'gift_cards.list.column.status': '状态',
  'gift_cards.list.column.amount': '面值',
  'gift_cards.list.column.batch': '批次',
  'gift_cards.list.column.redeemed_by': '兑换账户',
  'gift_cards.list.column.updated': '最近状态时间',
  'gift_cards.list.column.actions': '操作',
  'gift_cards.list.column.source': '来源',
  'gift_cards.list.show_code': '显示',
  'gift_cards.list.hide_code': '隐藏',
  'gift_cards.list.copy_code': '复制',
  'gift_cards.list.empty': '没有符合条件的礼品卡',
  'gift_cards.list.total': '共 {total} 张',
  'gift_cards.result.code': '兑换码',
  'gift_cards.result.face_value': '面值',
  'gift_cards.result.issued_at': '发行时间',
  'gift_cards.result.expires_at': '过期时间',
  'gift_cards.result.never': '永不过期',
  'gift_cards.result.batch_id': '批次 ID',
  'gift_cards.result.batch_id_empty': '—',
  'gift_cards.result.redeemed_at': '兑换时间',
  'gift_cards.result.redeemed_by': '兑换账户',
  'gift_cards.result.redeemed_by_empty': '—',
  'gift_cards.result.credit_value': '入账金额(USD)',
  'gift_cards.result.applied_rate': '锁定汇率',
  'gift_cards.result.revoked_at': '吊销时间',
  'gift_cards.result.revoke': '吊销',
  'gift_cards.result.redeem': '代为兑换',
  'gift_cards.revoke_modal.title': '吊销礼品卡?',
  'gift_cards.revoke_modal.body_prefix': '这将永久停用 ',
  'gift_cards.revoke_modal.body_separator': '(面值 ',
  'gift_cards.revoke_modal.body_suffix': ')。操作不可撤销。',
  'gift_cards.revoke_modal.reason.label': '原因(可选,写入审计日志)',
  'gift_cards.revoke_modal.reason.placeholder': '重复发行 / 误发 / 欺诈',
  'gift_cards.revoke_modal.submit': '吊销',
  'gift_cards.revoke_modal.submitting': '吊销中…',
  'gift_cards.revoke_modal.toast': '礼品卡 {code} 已吊销',
  'gift_cards.redeem_modal.title': '代为兑换',
  'gift_cards.redeem_modal.body_prefix': '该操作将把 ',
  'gift_cards.redeem_modal.body_middle': ' 入账到目标账户，礼品卡 ',
  'gift_cards.redeem_modal.body_suffix':
    ' 的人民币面值为 {faceValue}。资金立即在平台账本结算,只能通过余额调整反向操作。',
  'gift_cards.redeem_modal.account.label': '目标账户 ID(UUID)',
  'gift_cards.redeem_modal.account.placeholder': 'acct_…',
  'gift_cards.redeem_modal.confirm.label': '确认金额(请键入 {amount})',
  'gift_cards.redeem_modal.reason.label': '原因(可选)',
  'gift_cards.redeem_modal.reason.placeholder': '客服补偿 / 促销兑换',
  'gift_cards.redeem_modal.idempotency': '幂等 key:',
  'gift_cards.redeem_modal.submit': '入账 {amount}',
  'gift_cards.redeem_modal.submitting': '入账中…',
  'gift_cards.redeem_modal.toast': '已向 {account} 充值 {amount}——新余额 {balance}',
  'gift_cards.redeem_modal.error.account_required': '账户 ID 不能为空',
  'gift_cards.redeem_modal.error.amount_mismatch': '确认金额必须等于 {amount}(资金操作的二次确认)',
  'gift_cards.bulk.title': '批量操作记录',
  'gift_cards.bulk.body':
    '阶段 1 暂不跟踪批量操作。发行 / 吊销 / 兑换事件记录在平台账本和 gateway 审计日志中,批量视图待后续阶段。',
  'gift_cards.issue.error.amount_positive': '金额必须为正的 RMB 值',
  'gift_cards.issue.error.amount_whole_yuan': 'RMB 礼品卡金额必须为整元',
  'gift_cards.issue.error.expires_at_invalid': '过期时间必须是有效日期',
  'gift_cards.issue.error.expires_at_future': '过期时间必须在将来',

  // ── Accounts list ───────────────────────────────────────────────
  'accounts.title': '账户',
  'accounts.subtitle':
    '带有组织归属、当前余额、最近账本访问记录的租户账户。点击邮箱打开详情 / 余额调整面板。',
  'accounts.search.placeholder': '搜索邮箱…',
  'accounts.search.aria_label': '搜索账户邮箱',
  'accounts.has_balance': '有余额',
  'accounts.search.submit': '搜索',
  'accounts.search.clear': '清空',
  'accounts.error.title': '无法加载账户',
  'accounts.loading_aria': '正在加载账户…',
  'accounts.empty.filtered.title': '没有账户匹配当前筛选',
  'accounts.empty.filtered.body': '清空筛选或调整搜索条件。',
  'accounts.empty.initial.title': '暂无已注册账户',
  'accounts.empty.initial.body': '当租户通过公开 API 注册后,账户会出现在这里。',
  'accounts.column.email': '邮箱',
  'accounts.column.org_id': '组织 ID',
  'accounts.column.balance': '余额',
  'accounts.column.kind': '类型',
  'accounts.column.created': '创建时间',
  'accounts.chip.system': '系统',
  'accounts.chip.tenant': '租户',
  'accounts.pagination.aria_label': '分页',
  'accounts.pagination.range': '显示第 {start}–{end} 条,共 {total} 条',

  // ── Account detail ──────────────────────────────────────────────
  'account_detail.loading_aria': '正在加载账户…',
  'account_detail.error.title': '无法加载账户',
  'account_detail.back': '返回账户列表',
  'account_detail.breadcrumb.accounts': '账户',
  'account_detail.meta.created': '创建于 {iso}',
  'account_detail.meta.org': '组织 · {name}({role})',
  'account_detail.summary.title': '概要',
  'account_detail.summary.account_id': '账户 ID',
  'account_detail.summary.org_id': '组织 ID',
  'account_detail.summary.org_name': '组织名称',
  'account_detail.summary.member_role': '成员角色',
  'account_detail.summary.current_balance': '当前余额',
  'account_detail.tx.title': '最近交易',
  'account_detail.tx.last_n': '最近 {n} 条',
  'account_detail.tx.empty': '暂无账本记录。',
  'account_detail.tx.column.type': '类型',
  'account_detail.tx.column.amount': '金额',
  'account_detail.tx.column.actor': '操作者',
  'account_detail.tx.column.reason': '原因',
  'account_detail.tx.column.created': '创建时间',
  'account_detail.adjust.title': '余额调整',
  'account_detail.adjust.subtitle': '签名账本 · 负号代表扣款(允许)',
  'account_detail.adjust.amount.label': '金额(USD)',
  'account_detail.adjust.amount.placeholder': '25 或 -12.50',
  'account_detail.adjust.reason.label': '原因(必填,写入审计日志)',
  'account_detail.adjust.reason.placeholder': '客服 / 退款 / 更正…',
  'account_detail.adjust.reason.placeholder_refund':
    '例:订单 ABC-123 退款(备注会自动加 refund: 前缀)',
  'account_detail.adjust.reason.placeholder_sla_credit':
    '例:2026-Q2 故障补偿(备注会自动加 sla_credit: 前缀)',
  'account_detail.adjust.mode.aria_label': '调账模式',
  'account_detail.adjust.mode.custom': '自定义',
  'account_detail.adjust.mode.refund': '退款',
  'account_detail.adjust.mode.sla_credit': 'SLA 补偿',
  'account_detail.adjust.mode.custom.hint': '任意有符号金额 + 自由备注;允许负数(借记)。',
  'account_detail.adjust.mode.refund.hint':
    '仅正数金额;备注自动加 `refund:` 前缀,财务月结按此归类。',
  'account_detail.adjust.mode.sla_credit.hint':
    '仅正数金额;备注自动加 `sla_credit:` 前缀,供 SLA 月度复核。',
  'account_detail.adjust.error.positive_required':
    '退款 / SLA 补偿必须是正数;负数请切回自定义模式。',
  'account_detail.adjust.submit': '调整余额',
  'account_detail.adjust.error.amount_required': '金额不能为空',
  'account_detail.adjust.error.amount_non_zero': '金额必须是非零的 USD 值(扣款请加 `-` 前缀)',
  'account_detail.adjust.error.reason_required': '原因不能为空——会写入账本审计日志',
  'account_detail.confirm.title': '确认余额调整',
  'account_detail.confirm.target_account': '目标账户',
  'account_detail.confirm.current_balance': '当前余额',
  'account_detail.confirm.adjustment': '调整金额',
  'account_detail.confirm.resulting_balance': '调整后余额',
  'account_detail.confirm.type_to_confirm': '请原样键入金额以确认({raw})',
  'account_detail.confirm.idempotency_key': '幂等 key:',
  'account_detail.confirm.submitting': '提交中…',
  'account_detail.confirm.submit': '应用 {signed}',
  'account_detail.email_verification.title': '邮箱验证',
  'account_detail.email_verification.body':
    '客服动作会写入审计。优先重发验证邮件;只有人工核验后才标记已验证。',
  'account_detail.email_verification.status.verified': '已验证',
  'account_detail.email_verification.status.unverified': '未验证',
  'account_detail.email_verification.resend': '重发验证邮件',
  'account_detail.email_verification.resending': '重发中…',
  'account_detail.email_verification.reason_label': '人工验证原因',
  'account_detail.email_verification.reason_placeholder': '工单 / 用户证明',
  'account_detail.email_verification.mark_verified': '标记已验证',
  'account_detail.email_verification.marking': '标记中…',
  'account_detail.email_verification.support_link': '支持请求',
  'account_detail.email_verification.toast.resent': '验证邮件已重发',
  'account_detail.email_verification.toast.marked': '邮箱已标记为验证',
  'account_detail.email_verification.error.reason_required': '标记已验证前必须填写原因',

  // ── Account detail / API keys card (Epic #109 / P0.2) ───────────
  // ops 侧吊销 tenant API key。card 列出该账户所属 org 的全部 key
  // (含已吊销),对活跃 key 提供吊销操作,附强制 reason 输入。
  'account_detail.api_keys.title': 'API 密钥',
  'account_detail.api_keys.subtitle': '此 org 名下的全部 key(含已吊销)',
  'account_detail.api_keys.loading': '加载密钥中…',
  'account_detail.api_keys.load_error': '加载密钥失败',
  'account_detail.api_keys.empty': '此 org 尚未创建任何 API 密钥。',
  'account_detail.api_keys.col.name': '名称',
  'account_detail.api_keys.col.prefix': '前缀',
  'account_detail.api_keys.col.created_at': '创建时间',
  'account_detail.api_keys.col.last_used': '最近使用',
  'account_detail.api_keys.col.status': '状态',
  'account_detail.api_keys.col.actions': '操作',
  'account_detail.api_keys.unnamed': '(无名称)',
  'account_detail.api_keys.status.active': '活跃',
  'account_detail.api_keys.status.revoked': '已吊销',
  'account_detail.api_keys.revoke': '吊销',
  'account_detail.api_keys.revoked_meta': '已于 {iso} 吊销',
  'account_detail.api_keys.revoke_modal.title': '吊销 {name}?',
  'account_detail.api_keys.revoke_modal.body':
    '吊销后此 key 立刻失效,tenant 使用它的调用将返回 401。操作不可撤销;需要重发请创建新 key。',
  'account_detail.api_keys.revoke_modal.reason.label': '吊销原因(必填,写入审计日志)',
  'account_detail.api_keys.revoke_modal.reason.placeholder': '疑似泄露 / 滥用 / tenant 主动要求…',
  'account_detail.api_keys.revoke_modal.submit': '吊销密钥',
  'account_detail.api_keys.revoke_modal.submitting': '吊销中…',
  'account_detail.api_keys.toast.revoked': '密钥 {id} 已吊销',
  'account_detail.api_keys.toast.already_revoked': '密钥 {id} 此前已被吊销',

  // ── Failed deductions ───────────────────────────────────────────
  'failed_deductions.title': '扣款失败',
  'failed_deductions.subtitle':
    '审核未成功结算的扣款并补齐审计链。阶段 1 不重新扣款——仅登记决议用于账本核对。',
  'failed_deductions.filter.unresolved': '未处理',
  'failed_deductions.filter.unresolved.hint': '等待决议',
  'failed_deductions.filter.resolved': '已处理',
  'failed_deductions.filter.resolved.hint': '已登记决议',
  'failed_deductions.filter.all': '全部',
  'failed_deductions.filter.all.hint': '全部历史',
  'failed_deductions.error.title': '无法加载扣款失败记录',
  'failed_deductions.loading_aria': '正在加载扣款失败记录…',
  'failed_deductions.empty.unresolved': '无未处理扣款失败——一切正常',
  'failed_deductions.empty.resolved': '暂无已处理记录',
  'failed_deductions.empty.all': '未记录任何扣款失败',
  'failed_deductions.empty.hint': '当账户在计费周期内余额转负时,新纪录会进入这里。',
  'failed_deductions.column.account': '账户',
  'failed_deductions.column.request_id': '请求 ID',
  'failed_deductions.column.amount': '金额',
  'failed_deductions.column.reason': '原因',
  'failed_deductions.column.created': '发生时间',
  'failed_deductions.column.resolved': '决议时间',
  'failed_deductions.column.actions': '操作',
  'failed_deductions.row.resolve': '决议',
  'failed_deductions.row.closed': '已关闭',
  'failed_deductions.resolve_modal.title': '决议扣款失败',
  'failed_deductions.resolve_modal.body_prefix': '账户 ',
  'failed_deductions.resolve_modal.body_separator': ' · 金额 ',
  'failed_deductions.resolve_modal.body_suffix': '。Phase-1 决议仅用于审计——不会尝试扣款。',
  'failed_deductions.resolve_modal.legend': '决议方式',
  'failed_deductions.resolve_modal.forgive': '免除',
  'failed_deductions.resolve_modal.chargeback': '拒付',
  'failed_deductions.resolve_modal.retry': '重试',
  'failed_deductions.resolve_modal.forgive.hint': '免除——冲销负余额,不重新扣款',
  'failed_deductions.resolve_modal.chargeback.hint': '拒付——登记租户已争议,不重新扣款',
  'failed_deductions.resolve_modal.retry.hint': '重试——备注已手动完成重新扣款',
  'failed_deductions.resolve_modal.submitting': '决议中…',
  'failed_deductions.resolve_modal.submit': '登记 {choice}',
  'billing_anomalies.title': '账务异常',
  'billing_anomalies.subtitle': '租户已经收到成功响应，但账务结算还需要自动补偿或人工跟进的请求。',
  'billing_anomalies.filter.unresolved': '未处理',
  'billing_anomalies.filter.unresolved.hint': '仍待结算',
  'billing_anomalies.filter.resolved': '已处理',
  'billing_anomalies.filter.resolved.hint': '已补偿或已升级',
  'billing_anomalies.filter.all': '全部',
  'billing_anomalies.filter.all.hint': '完整异常历史',
  'billing_anomalies.error.title': '无法加载账务异常',
  'billing_anomalies.empty.unresolved': '当前没有未处理账务异常',
  'billing_anomalies.empty.resolved': '暂无已处理账务异常',
  'billing_anomalies.empty.all': '尚未记录账务异常',
  'billing_anomalies.empty.hint': '健康请求应直接结算为已扣费或显式零成本，而不是进入这个队列。',
  'billing_anomalies.column.account': '账户',
  'billing_anomalies.column.request_id': '请求 ID',
  'billing_anomalies.column.model': '模型',
  'billing_anomalies.column.amount': '金额',
  'billing_anomalies.column.reason': '原因',
  'billing_anomalies.column.replay': '重放状态',
  'billing_anomalies.column.created': '发生时间',
  'billing_anomalies.column.resolved': '处理时间',
  'billing_anomalies.pagination.summary': '显示 {shown} / 共 {total} 条',

  // ── Payments orders ─────────────────────────────────────────────
  'payments_orders.title': '支付订单',
  'payments_orders.subtitle': '查阅租户充值并对未收到 ZPay 回调的订单进行对账。',
  'payments_orders.status.all': '全部',
  'payments_orders.status.pending': '待支付',
  'payments_orders.status.paid': '已支付',
  'payments_orders.status.expired': '已过期',
  'payments_orders.status.failed': '失败',
  'payments_orders.status.refunded': '已退款',
  'payments_orders.filter.account_id': '账户 ID',
  'payments_orders.filter.from': '起始时间(ISO-8601)',
  'payments_orders.filter.to': '结束时间(ISO-8601)',
  'payments_orders.loading_aria': '正在加载支付订单…',
  'payments_orders.error.title': '无法加载支付订单',
  'payments_orders.empty': '没有订单匹配当前筛选。',
  'payments_orders.column.created': '创建时间',
  'payments_orders.column.account': '账户',
  'payments_orders.column.status': '状态',
  'payments_orders.column.usd': 'USD',
  'payments_orders.column.cny': 'CNY',
  'payments_orders.column.rate': '汇率',
  'payments_orders.column.pay': '支付方式',
  'payments_orders.column.paid_at': '支付时间',
  'payments_orders.column.order_id': '订单 ID',
  'payments_orders.column.actions': '操作',
  'payments_orders.row.details': '详情',
  'payments_orders.row.collapse': '收起',
  'payments_orders.row.reconcile': '对账',
  'payments_orders.showing': '共 {n} 条订单',
  'payments_orders.load_more': '加载更多',
  'payments_orders.loading_more': '加载中…',
  'payments_orders.reconcile_modal.title': '对账支付订单',
  'payments_orders.filter.status_label': '状态',
  'payments_orders.filter.status_aria_label': '状态筛选',
  'payments_orders.filter.account_id.placeholder': 'acc_...',
  'payments_orders.filter.to.placeholder': '(无结束时间)',
  'payments_orders.row.reconcile_tooltip.available': '强制向 ZPay 对账,如成功则立即结算',
  'payments_orders.row.reconcile_tooltip.disabled': '仅对待支付订单可发起对账',
  'payments_orders.reconcile_modal.body':
    '我们会向 ZPay 询问订单 {orderId} 是否已支付。若是,则将 {amount} 原子结算至该账户;若否,则不作变更。',
  'payments_orders.reconcile_modal.account': '账户',
  'payments_orders.reconcile_modal.usd': 'USD',
  'payments_orders.reconcile_modal.cny': 'CNY',
  'payments_orders.reconcile_modal.applied_rate': '已应用汇率',
  'payments_orders.reconcile_modal.note.label': '备注(可选,计入审计)',
  'payments_orders.reconcile_modal.note.placeholder':
    '例如:客户自述已通过 ZPay 支付但未收到 notify',
  'payments_orders.reconcile_modal.type_to_confirm': '键入订单号以确认',
  'payments_orders.reconcile_modal.type_hint_prefix': '精确粘贴或原样键入 ',
  'payments_orders.reconcile_modal.type_hint_suffix': ' 方可激活「对账」按钮。',
  'payments_orders.reconcile_modal.submitting': '对账中…',
  'payments_orders.reconcile_modal.submit': '立即对账',
  'payments_orders.reconcile_modal.detail.order_id': '订单 ID',
  'payments_orders.reconcile_modal.detail.out_trade_no': '内部交易号',
  'payments_orders.reconcile_modal.detail.upstream_trade_no': '上游交易号',
  'payments_orders.reconcile_modal.detail.expired_at': '过期时间',
  'payments_orders.reconcile_modal.detail.empty': '—',
  'payments_orders.reconcile_modal.toast.credited': '订单 {orderId} 已结算(状态={status})',
  'payments_orders.reconcile_modal.toast.upstream': '上游报告状态={status};订单 {orderId} 未入账',
  'payments_orders.reconcile_modal.toast.already': '订单 {orderId} 已处于状态={status};无需操作',

  // ── Payment providers ───────────────────────────────────────────
  'payment_providers.title': '支付通道',
  'payment_providers.subtitle':
    '管理每家支付聚合的凭证与端点 URL。变更在 60 秒内生效——轮换密钥后 platform-api 会自动拉取最新版本。',
  'payment_providers.new': '新建通道',
  'payment_providers.banner':
    '变更在 60 秒内生效——轮换密钥后 platform-api 会自动拉取最新版本,无需重新部署。',
  'payment_providers.empty.title': '尚未配置任何支付通道。',
  'payment_providers.empty.body':
    '添加第一个通道以开启租户充值。在此之前 /v1/billing/topup 将返回 503。',
  'payment_providers.empty.cta': '新增通道',
  'payment_providers.row.rotate': '轮换',
  'payment_providers.row.pid': 'PID',
  'payment_providers.row.reveal': '查看',
  'payment_providers.row.revealing': '查看中…',
  'payment_providers.row.revealed': '已显示',
  'payment_providers.chip.active': '活跃',
  'payment_providers.chip.retired': '停用',
  'payment_providers.create.title': '新增通道',
  'payment_providers.create.provider': '通道',
  'payment_providers.create.pid': '商户 ID(PID)',
  'payment_providers.create.secret': '密钥',
  'payment_providers.create.submit_url': '提交 URL',
  'payment_providers.create.mapi_url': 'MAPI URL',
  'payment_providers.create.query_url': '查询 URL',
  'payment_providers.create.note': '备注(可选,计入审计)',
  'payment_providers.create.active_checkbox': '将此条凭证设为 {provider} 的活跃版本',
  'payment_providers.create.rotation_warning': '已有此通道的活跃凭证——新建将轮换并停用旧版。',
  'payment_providers.create.error.pid_required': 'PID 不能为空',
  'payment_providers.create.error.secret_required': '密钥不能为空',
  'payment_providers.loading_aria': '正在加载支付通道…',
  'payment_providers.error.title': '无法加载支付通道',
  'payment_providers.create.modal.title.new': '新增 {provider} 通道',
  'payment_providers.create.modal.title.rotate': '轮换 {provider} 凭证',
  'payment_providers.create.secret.hint':
    '用于 MD5 签名。D1 明文存储是有意为之——运维可无发布自行轮换。',
  'payment_providers.create.mapi_url.optional_suffix': '(可选)',
  'payment_providers.create.active_hint.rotation': '这将停用当前活跃记录。需键入确认。',
  'payment_providers.create.active_hint.retired': '不影响当前活跃记录——此条凭证保存为停用状态。',
  'payment_providers.create.active_hint.first': '当前无活跃记录,此条将成为首个活跃凭证。',
  'payment_providers.create.error.submit_url_required': '提交 URL 不能为空',
  'payment_providers.create.error.query_url_required': '查询 URL 不能为空',
  'payment_providers.create.error.mapi_url_required_zpay': 'zpay 必须配置 MAPI URL',
  'payment_providers.create.submit.save': '保存',
  'payment_providers.create.submit.continue': '继续',
  'payment_providers.confirm.title.rotation': '确认轮换 {provider} 凭证',
  'payment_providers.confirm.title.save': '保存 {provider} 凭证',
  'payment_providers.confirm.body.rotation':
    '即将替换线上凭证,后续签名调用将在 60 秒内使用新的 PID + 密钥。',
  'payment_providers.confirm.body.save': '此记录将保存,不影响当前活跃凭证。',
  'payment_providers.confirm.type_prefix': '键入 ',
  'payment_providers.confirm.type_suffix': ' 以确认',
  'payment_providers.confirm.back': '返回',
  'payment_providers.confirm.submit.saving': '保存中…',
  'payment_providers.confirm.submit.rotate': '轮换凭证',
  'payment_providers.confirm.submit.save': '保存',
  'payment_providers.confirm.error.type_mismatch': '请原样键入 {provider} 以确认轮换',
  'payment_providers.toast.rotated': '{provider} 凭证已轮换(停用 {retiredId})',
  'payment_providers.toast.saved': '{provider} 凭证已保存({status})',
  'payment_providers.toast.status.active': '活跃',
  'payment_providers.toast.status.retired': '停用',

  // ── Pricing (exchange rate policy) ──────────────────────────────
  'pricing.title': '汇率策略',
  'pricing.subtitle': '充值换算汇率(R)——客户支付多少 CNY 换得 $1 USD 余额。',
  'pricing.policy.title': '当前汇率策略',
  'pricing.policy.empty': '尚未设置策略。使用下方表单确立初始汇率。',
  'pricing.policy.pair': '货币对',
  'pricing.policy.current_r': '当前 R',
  'pricing.policy.updated_by': '更新人',
  'pricing.policy.updated_at': '更新时间',
  'pricing.policy.meaning.title': '含义',
  'pricing.policy.meaning.body': '客户支付 ¥{rate} 换得 $1.00 USD 余额。',
  'pricing.market.title': '市场参考',
  'pricing.market.subtitle': '每 6 小时通过 cron 更新',
  'pricing.market.empty': '尚未采集到市场样本——cron 上线后会自动填充。',
  'pricing.market.latest_rate': '最新市场价',
  'pricing.market.source': '来源',
  'pricing.market.sampled_at': '采样时间',
  'pricing.market.spread': '与市场价差',
  'pricing.edit.title': '调整汇率',
  'pricing.edit.subtitle': '输入确认 · 计入审计',
  'pricing.edit.new_r': '新 R(每 USD CNY)',
  'pricing.edit.note': '备注(可选,计入审计)',
  'pricing.edit.submit': '保存',
  'pricing.confirm.title': '确认汇率调整',
  'pricing.confirm.previous': '旧汇率',
  'pricing.confirm.new': '新汇率',
  'pricing.confirm.market': '市场参考',
  'pricing.confirm.projected_spread': '预计价差',
  'pricing.confirm.note': '备注',
  'pricing.loading_aria': '正在加载汇率策略…',
  'pricing.error.title': '无法加载汇率策略',
  'pricing.policy.note_prefix': '备注:',
  'pricing.edit.rate_placeholder': '7.89',
  'pricing.edit.note_placeholder': '跟随市场波动调整',
  'pricing.edit.error.required': '新汇率不能为空',
  'pricing.edit.error.not_number': '新汇率必须是数值',
  'pricing.edit.error.out_of_range': '新汇率必须在 ({min}, {max}] 区间——请确认小数点位置',
  'pricing.confirm.type_hint': '原样键入新汇率以确认({rate})',
  'pricing.confirm.error.type_mismatch': '请原样键入新汇率({rate})以确认',
  'pricing.confirm.submit.saving': '保存中…',
  'pricing.confirm.submit.save': '保存 {rate}',
  'pricing.toast.updated': '汇率已更新为 {rate}',

  // ── Revenue ─────────────────────────────────────────────────────
  'revenue.title': '收入',
  'revenue.subtitle': '跨全部租户与模型的 L1(充值汇率溢价)+ L2(Token 销售毛利)。',
  'revenue.window.today': '今日',
  'revenue.window.7d': '最近 7 天',
  'revenue.window.30d': '最近 30 天',
  'revenue.summary.title': '概要',
  'revenue.summary.subtitle': '所选窗口的 L1 溢价 + L2 毛利。',
  'revenue.summary.topup_gmv': '充值 GMV',
  'revenue.summary.usage_gmv': '用量 GMV',
  'revenue.summary.l1_profit': 'L1 利润',
  'revenue.summary.channel_fees': '通道手续费',
  'revenue.summary.usd_credited': 'USD 入账',
  'revenue.summary.l2_profit': 'L2 利润',
  'revenue.summary.upstream_cost': '上游成本',
  'revenue.summary.total': '总毛利',
  'revenue.daily.title': '每日趋势',
  'revenue.daily.subtitle': '按天堆叠 L1(主)+ L2(三级)利润。',
  'revenue.l2.title': '按模型 L2',
  'revenue.l2.subtitle': '按模型统计用量毛利。亏损模型自动标记。',
  'revenue.l2.currency.label': '币种',
  'revenue.l2.currency.aria': '按模型 L2 展示币种',
  'revenue.l2.column.model': '模型',
  'revenue.l2.column.usage': '用量',
  'revenue.l2.column.usd_charged': 'USD 计费',
  'revenue.l2.column.cny_cost': 'CNY 成本',
  'revenue.l2.column.charged': '计费收入 ({currency})',
  'revenue.l2.column.cost': '上游成本 ({currency})',
  'revenue.l2.column.l2_profit': 'L2 利润',
  'revenue.l2.column.l2_pct': 'L2 %',
  'revenue.l2.flag.loss': 'LOSS',
  'revenue.l2.flag.thin': 'THIN',
  'revenue.top_tenants.title': 'Top 租户',
  'revenue.top_tenants.subtitle': '按租户的终身 L1 + L2 累计贡献。按 CNY 毛利总额排序,前 {n} 名。',
  'revenue.top_tenants.column.email': '邮箱',
  'revenue.top_tenants.column.topups': '充值',
  'revenue.top_tenants.column.l1_profit': 'L1 利润',
  'revenue.top_tenants.column.usage': '用量',
  'revenue.top_tenants.column.l2_profit': 'L2 利润',
  'revenue.top_tenants.column.total': '合计',
  'revenue.summary.window_aria': '概要窗口',
  'revenue.summary.error_label': '无法加载概要',
  'revenue.summary.topup.orders': '{n} 笔订单',
  'revenue.summary.usage.api_calls': '{n} 次 API 调用',
  'revenue.daily.subtitle_full': '按天堆叠 L1(主)+ L2(三级)利润。按 UTC 分桶。{days} 天。',
  'revenue.daily.error_label': '无法加载每日趋势',
  'revenue.daily.empty': '尚无收入数据',
  'revenue.daily.aria_label': '每日收入趋势——{days} 天',
  'revenue.daily.tooltip': '{date}——L1 {l1}({topupCount} 笔充值),L2 {l2}({usageCount} 次用量)',
  'revenue.l2.error_label': '无法加载模型毛利分布',
  'revenue.l2.empty': '所选窗口内无用量记录。',
  'revenue.top_tenants.error_label': '无法加载 Top 租户',
  'revenue.top_tenants.empty': '尚无租户产生毛利贡献。',
  'revenue.top_tenants.no_email': '(无邮箱)',

  // ── Audit log ───────────────────────────────────────────────────
  'audit.title': '审计日志',
  'audit.subtitle':
    'ops-admin 所有变更的只追加审计链。服务端按 `when` 倒序(最新在前);筛选字段仅支持精确匹配。',
  'audit.filter.action': '动作',
  'audit.filter.target_type': '目标类型',
  'audit.filter.target_id': '目标 ID',
  'audit.filter.ops_user_id': 'ops 用户 ID',
  'audit.filter.target_type.any': '任意目标类型',
  'audit.filter.target_type.account': 'account',
  'audit.filter.target_type.gift_card': 'gift-card',
  'audit.filter.target_type.gift_card_batch': 'gift-card-batch',
  'audit.filter.target_type.upstream': 'upstream',
  'audit.filter.target_type.upstream_model_mapping': 'upstream-model-mapping',
  'audit.filter.target_type.upstream_funding_batch': 'upstream-funding-batch',
  'audit.filter.target_type.model': 'model',
  'audit.filter.target_type.failed_deduction': 'failed-deduction',
  'audit.filter.target_type.exchange_rate_policy': 'exchange-rate-policy',
  'audit.filter.target_type.payment_order': 'payment-order',
  'audit.filter.target_type.payment_provider': 'payment-provider',
  'audit.filter.apply': '应用',
  'audit.filter.reset': '重置',
  'audit.filter.action.placeholder': '例如 balance.adjust',
  'audit.filter.ops_user_id.placeholder': 'ops_xxx',
  'audit.filter.target_id.placeholder': '精确匹配',
  'audit.error.title': '无法加载审计日志',
  'audit.loading_aria': '正在加载审计日志…',
  'audit.empty.title': '没有行匹配当前筛选',
  'audit.empty.body': '放宽或清空筛选条件再试。',
  'audit.column.when': '时间',
  'audit.column.action': '动作',
  'audit.column.target_type': '目标类型',
  'audit.column.target_id': '目标 ID',
  'audit.column.ops_user': 'ops 用户',
  'audit.pagination.range': '显示第 {start}–{end} 条,共 {total} 条',

  // ── Upstream detail tabs + summary stats ────────────────────────────
  'upstream_detail.tab.config': '配置',
  'upstream_detail.tab.mappings': '模型路由',
  'upstream_detail.tab.funding': '资金',
  'upstream_detail.tab.probe': '探针',
  'upstream_detail.summary.source': '客服来源码',
  'upstream_detail.summary.endpoint': '端点',
  'upstream_detail.summary.accounts': '账户',
  'upstream_detail.summary.accounts_unit': '个',
  'upstream_detail.summary.mappings': '模型映射',
  'upstream_detail.summary.mappings_unit': '条',
  'upstream_detail.summary.funding': '资金',
  'upstream_detail.summary.funding_remaining': '剩余',
  'upstream_detail.summary.funding_none': '无资金批次',

  // ── Upstream maintenance toggle (P2.11) ────────────────────────────
  'upstream_detail.maintenance.label': '运行状态',
  'upstream_detail.maintenance.state.enabled': '正在路由流量',
  'upstream_detail.maintenance.state.disabled': '维护中 · 网关已停止路由',
  'upstream_detail.maintenance.enter.button': '进入维护',
  'upstream_detail.maintenance.resume.button': '恢复路由',
  'upstream_detail.maintenance.modal.title_enter': '把 {name} 置入维护?',
  'upstream_detail.maintenance.modal.title_resume': '恢复 {name} 的路由?',
  'upstream_detail.maintenance.modal.body_enter':
    '立即生效:gateway 选择器会把该上游从候选集中剔除。已在途的请求不受影响;新请求将改走其他候选。若是单点 SKU,租户会直接 404(见仪表盘告警)。',
  'upstream_detail.maintenance.modal.body_resume':
    '恢复后该上游重新进入候选集,选择器按原 SLA × 成本规则分配流量。',
  'upstream_detail.maintenance.impact.label': '当前模型路由映射 · {count}',
  'upstream_detail.maintenance.impact.none': '无模型映射;停用不会立即影响任何 SKU。',
  'upstream_detail.maintenance.confirm.type_label': '输入 `maintenance` 确认',
  'upstream_detail.maintenance.confirm.type_mismatch': '请准确输入 maintenance',
  'upstream_detail.maintenance.confirm.submit_enter': '确认停用',
  'upstream_detail.maintenance.confirm.submit_resume': '确认恢复',

  // ── Account lifecycle: suspend / delete (P2.10) ────────────────────
  'account_lifecycle.title': '账号生命周期',
  'account_lifecycle.status.active': '活跃',
  'account_lifecycle.status.suspended': '已停用',
  'account_lifecycle.status.deleted': '已删除',
  'account_lifecycle.suspended.banner': '{iso} 起停用 · 原因:{reason}',
  'account_lifecycle.suspended.no_reason': '(未填写)',
  'account_lifecycle.deleted.banner': '{iso} 起软删除,租户 API 请求将返回 403 ACCOUNT_DELETED。',
  'account_lifecycle.suspend.reason_label': '停用原因(可选)',
  'account_lifecycle.suspend.reason_placeholder': '例:滥用举报 2026-04-20',
  'account_lifecycle.suspend.reason_hint':
    '会写入 accounts.suspension_reason,供日后追溯;同时落一条 ops_audit 记录。',
  'account_lifecycle.suspend.button': '停用',
  'account_lifecycle.unsuspend.button': '解除停用',
  'account_lifecycle.delete.button': '软删除',
  'account_lifecycle.confirm.title_suspend': '停用 {email}?',
  'account_lifecycle.confirm.body_suspend':
    '立即停用该租户:/internal/auth/verify-key 会返回 ACCOUNT_SUSPENDED,所有新请求被拒。历史 usage_logs / 账户余额保留不动。',
  'account_lifecycle.confirm.title_unsuspend': '解除 {email} 的停用?',
  'account_lifecycle.confirm.body_unsuspend':
    '清除 suspended_at + suspension_reason,租户立即恢复正常路由。',
  'account_lifecycle.confirm.title_delete': '软删除 {email}?',
  'account_lifecycle.confirm.body_delete':
    '标记 deleted_at;租户 API 请求从此 403 ACCOUNT_DELETED。历史 usage_logs / balance_transactions 保留(用于对账)。完整 GDPR 擦除(清邮箱 + 密码哈希)是后续任务。',
  'account_lifecycle.confirm.type_email': '输入 {email} 确认',
  'account_lifecycle.confirm.type_mismatch': '请准确输入 {email}',
  'account_lifecycle.confirm.reason_preview': '停用原因预览:{reason}',
  'account_lifecycle.confirm.submit_suspend': '确认停用',
  'account_lifecycle.confirm.submit_unsuspend': '确认解除停用',
  'account_lifecycle.confirm.submit_delete': '确认软删除',
  'account_lifecycle.toast.suspended': '{email} 已停用',
  'account_lifecycle.toast.unsuspended': '{email} 已解除停用',
  'account_lifecycle.toast.deleted': '{email} 已软删除',

  // ── Org routing policy (P2.9) ──────────────────────────────────────
  'routing_policy.title': '组织路由策略',
  'routing_policy.body':
    '本策略作用于组织 `{org}` 的所有租户账号,控制 SLA×成本选择器。留空=使用默认值;明确设为 0 与未设是不同语义。策略由网关 D1 直接读取,写入后生效几秒内。',
  'routing_policy.field.allowlist': '上游白名单',
  'routing_policy.field.allowlist.placeholder': 'ups_primary, ups_backup',
  'routing_policy.field.allowlist.hint': '逗号分隔;仅这些上游可被选中。留空=不启用白名单。',
  'routing_policy.field.denylist': '上游黑名单',
  'routing_policy.field.denylist.placeholder': 'ups_retired',
  'routing_policy.field.denylist.hint': '逗号分隔;这些上游会被从候选集剔除。',
  'routing_policy.field.sla_weight': 'SLA 权重(0–1)',
  'routing_policy.field.cost_weight': '成本权重(0–1)',
  'routing_policy.field.weight.placeholder': '留空=默认',
  'routing_policy.field.min_availability_pct': '最低可用率(%)',
  'routing_policy.field.max_p95_latency_ms': '最大 p95 延迟(毫秒)',
  'routing_policy.field.max_p95.placeholder': '留空=不限',
  'routing_policy.empty': '当前无策略行,使用全局默认值。',
  'routing_policy.updated_at': '上次更新:{iso}',
  'routing_policy.save': '保存',
  'routing_policy.clear': '清除(恢复默认)',
  'routing_policy.clear.pending': '清除中…',
  'routing_policy.error.weight_range': '权重必须在 [0, 1] 区间,留空代表使用默认。',
  'routing_policy.error.pct_range': '最低可用率必须在 [0, 100] 区间。',
  'routing_policy.error.latency_integer': '最大 p95 延迟必须是非负整数(毫秒),留空代表不限。',
  'routing_policy.toast.saved': '组织 `{org}` 路由策略已保存',
  'routing_policy.toast.cleared': '组织 `{org}` 路由策略已清除,将使用默认值',

  // ── Upstream onboarding wizard (P1.5) ─────────────────────────────
  'upstream_wizard.title': '新建上游接入',
  'upstream_wizard.subtitle':
    '四步把上游从 0 带到可路由:基础信息 → 模型路由 → 登记资金 → 探针自检。每一步都即时落库,中途可离开不会锁死。',
  'upstream_wizard.breadcrumb.upstreams': '上游',
  'upstream_wizard.breadcrumb.new': '新建',
  'upstream_wizard.progress.aria_label': '接入向导进度',
  'upstream_wizard.back': '上一步',
  'upstream_wizard.next': '下一步',
  'upstream_wizard.step.basic.title': '基础信息',
  'upstream_wizard.step.basic.heading': '创建上游记录',
  'upstream_wizard.step.basic.body':
    '创建 `upstream_configs` 行,保存鉴权密钥。`enabled` 默认关闭,最后一步才打开。',
  'upstream_wizard.step.basic.name': '显示名称',
  'upstream_wizard.step.basic.name.placeholder': 'anthropic-direct-a',
  'upstream_wizard.step.basic.endpoint': '端点 URL',
  'upstream_wizard.step.basic.endpoint.placeholder': 'https://api.anthropic.com',
  'upstream_wizard.step.basic.priority': '优先级',
  'upstream_wizard.step.basic.visibility': '租户可见范围',
  'upstream_wizard.step.basic.cookie_passthrough': 'Cookie 透传',
  'upstream_wizard.step.basic.cookie_passthrough.body': '允许租户 Cookie 原样透传到这个上游。',
  'upstream_wizard.step.basic.cookie_passthrough.hint':
    '默认关闭。只有在该上游确实依赖租户 Cookie 时才开启。',
  'upstream_wizard.step.basic.submit': '创建上游',
  'upstream_wizard.step.basic.submit.pending': '创建中…',
  'upstream_wizard.step.basic.error.required': '名称和端点为必填',
  'upstream_wizard.step.basic.error.priority': '优先级必须是非负整数',
  'upstream_wizard.step.keys.title': '添加密钥',
  'upstream_wizard.step.keys.heading': '为 {name} 添加 API 密钥',
  'upstream_wizard.step.keys.body': '至少添加一个密钥桶,网关会在桶中随机选取密钥转发请求。',
  'upstream_wizard.step.keys.count': '已添加 {count} 个密钥桶',
  'upstream_wizard.step.keys.label': '标签',
  'upstream_wizard.step.keys.label.placeholder': '如: primary-key',
  'upstream_wizard.step.keys.add': '添加',
  'upstream_wizard.step.keys.toast.added': '密钥桶已添加',
  'upstream_wizard.step.keys.error.min_one': '至少添加一个密钥桶才能继续',
  'upstream_wizard.step.keys.error.secret_required': '请输入 API 密钥',
  'upstream_wizard.step.models.title': '关联模型',
  'upstream_wizard.step.models.heading': '为 {name} 关联模型路由',
  'upstream_wizard.step.models.body':
    '选择厂商、渠道和模型,建立上游路由映射。至少添加一条才能继续。',
  'upstream_wizard.step.models.add': '添加映射',
  'upstream_wizard.step.models.toast.added': '模型映射已添加',
  'upstream_wizard.step.models.error.min_one': '至少添加一条模型映射才能继续',
  'upstream_wizard.step.probe.title': '探针自检',
  'upstream_wizard.step.probe.heading': '对 {name} 运行探针',
  'upstream_wizard.step.probe.body':
    '选一个模型路由映射运行一次真实请求。探针失败不会阻止启用——但强烈建议先排查。成功后点完成,会把 enabled 翻为 true 并跳转到详情页。',
  'upstream_wizard.step.probe.target.label': '探针目标',
  'upstream_wizard.step.probe.run': '运行探针',
  'upstream_wizard.step.probe.run.pending': '探针中…',
  'upstream_wizard.step.probe.finish': '完成并启用',
  'upstream_wizard.step.probe.finish.pending': '启用中…',
  'upstream_wizard.step.probe.warning.failed':
    '探针失败。确认后再启用——可在详情页修改配置,重新探针。',
  'upstream_wizard.toast.created': '上游 {name} 已创建(未启用)',
  'upstream_wizard.toast.attached': '{vendor}/{model} 已映射',
  'upstream_wizard.toast.funded': '资金批次已登记',
  'upstream_wizard.toast.enabled': '{name} 已启用——网关开始路由',

  // ── Support: per-tenant request query (P0.4) ─────────────────────
  'support.requests.title': '租户请求查询',
  'support.requests.subtitle':
    '按账户查询网关请求日志,用于账单争议排查。仅对 ops-admin 与 ops-support 角色开放。',
  'support.requests.filter.account_id': '账户 ID',
  'support.requests.filter.account_id.placeholder': 'acc_xxx',
  'support.requests.filter.from': '起始时间',
  'support.requests.filter.to': '结束时间',
  'support.requests.filter.request_id': '请求 ID(可选,精确匹配)',
  'support.requests.filter.request_id.placeholder': 'req_xxx',
  'support.requests.filter.apply': '应用',
  'support.requests.filter.reset': '重置',
  'support.requests.cta.title': '请输入账户 ID',
  'support.requests.cta.body':
    '在上方过滤器中填写目标账户 ID 并点击「应用」。时间范围可选,默认查询最近 24 小时;填入请求 ID 时将忽略时间范围并执行精确查找。',
  'support.requests.error.title': '无法加载请求日志',
  'support.requests.loading_aria': '正在加载请求日志…',
  'support.requests.account.account_id': '账户',
  'support.requests.account.email': '邮箱',
  'support.requests.account.org': '组织',
  'support.requests.account.org.none': '(无组织)',
  'support.requests.empty.title': '所选范围内无请求',
  'support.requests.empty.body': '放宽时间范围或清空请求 ID 再试。',
  'support.requests.column.when': '时间',
  'support.requests.column.request_id': '请求 ID',
  'support.requests.column.model': '模型',
  'support.requests.column.status': '状态',
  'support.requests.column.latency': '延迟',
  'support.requests.column.cost': '成本',
  'support.requests.column.api_key': 'API Key',
  'support.requests.column.upstream': '上游',
  'support.requests.row.api_key.unnamed': '(未命名)',
  'support.requests.row.upstream.deleted': '(已删除)',
  'support.requests.detail.request_id': '请求 ID',
  'support.requests.detail.upstream_id': '上游 ID',
  'support.requests.detail.protocol': '协议',
  'support.requests.detail.created_at': '创建时间',
  'support.requests.detail.input_tokens': '输入 tokens',
  'support.requests.detail.output_tokens': '输出 tokens',
  'support.requests.detail.cache_read': '缓存读',
  'support.requests.detail.cache_write': '缓存写',
  'support.requests.pagination.aria_label': '请求日志分页',
  'support.requests.pagination.range': '显示第 {start}–{end} 条,共 {total} 条',

  // ── Probe modal + result panel ──────────────────────────────────
  // Rendered by `ProbeModal` (shared dialog) and `ProbeResultPanel`
  // (dialog + upstream detail page). `probe.outcome.*` mirrors the
  // `ProbeOutcome` union from @nadoutong/contracts.
  'probe.outcome.pass': '通过',
  'probe.outcome.fail_shape': '格式错误',
  'probe.outcome.fail_content': '内容不符',
  'probe.outcome.fail_upstream_error': '上游错误',
  'probe.outcome.fail_timeout': '超时',
  'probe.id_label': '探针 {id}…',
  'probe.latency': '延迟',
  'probe.cost': '成本',
  'probe.upstream_status': '上游状态',
  'probe.input_output_tokens': '输入 / 输出 tokens',
  'probe.cache_read_write': '缓存读 / 写',
  'probe.charge_skipped': '跳过扣费',
  'probe.error': '错误',
  'probe.modal.title': '探测上游',
  'probe.modal.close_aria': '关闭探针对话框',
  'probe.modal.error.vendor_model_required': '厂商与模型均为必填',
  'probe.modal.vendor.label': '厂商',
  'probe.modal.vendor.placeholder': '例如 anthropic',
  'probe.modal.model.label': '模型',
  'probe.modal.model.placeholder': '例如 claude-sonnet-4-5',
  'probe.modal.protocol.label': '协议',
  'probe.modal.submit': '运行探针',
  'probe.modal.submitting': '探测中…',

  // ── Reports ─────────────────────────────────────────────────────
  'reports.title': '报表',
  'reports.subtitle': '合规与财务报表。按月聚合，支持 CSV 导出。',
  'reports.month_label': '月份',
  'reports.regulatory.title': '监管数据提取',
  'reports.regulatory.body':
    '用于 PRC 生成式 AI 备案的按账户月度使用数据。仅列出当月有使用记录的账户。',
  'reports.regulatory.csv_download': '下载 CSV',
  'reports.regulatory.loading': '正在加载监管数据…',
  'reports.regulatory.error': '加载监管数据失败。',
  'reports.regulatory.empty': '本月无账户使用记录。',
  'reports.regulatory.row_count': '{count} 个账户',
  'reports.regulatory.truncated': '行数超出导出上限，部分账户被截断。请提高上限或分页。',
  'reports.regulatory.column.account_id': '账户 ID',
  'reports.regulatory.column.email': '邮箱',
  'reports.regulatory.column.registered_at': '注册时间',
  'reports.regulatory.column.input_tokens': '输入 tokens',
  'reports.regulatory.column.output_tokens': '输出 tokens',
  'reports.regulatory.column.cost_usd': '成本 (USD)',
  'reports.monthly.title': '月度对账',
  'reports.monthly.body': '所选月份的收入台账 ↔ 支付订单。若有差额请排查。',
  'reports.monthly.csv_download': '下载 CSV',
  'reports.monthly.loading': '正在加载月度报表…',
  'reports.monthly.error': '加载月度报表失败。',
  'reports.monthly.drift.title': '检测到差额。',
  'reports.monthly.drift.body': '实收金额与确认充值金额相差 {amount}。请排查缺失的回调或台账写入。',
  'reports.monthly.tile.paid_cny': '已支付订单 (CNY)',
  'reports.monthly.tile.paid_count': '{count} 笔订单',
  'reports.monthly.tile.topups_cny_gross': '充值 (CNY 毛额)',
  'reports.monthly.tile.topups_count': '{count} 条台账',
  'reports.monthly.tile.usage_usd': '使用扣费 (USD)',
  'reports.monthly.tile.usage_rows': '{rows} 条使用记录',
  'reports.monthly.tile.l2_profit': 'L2 利润 (CNY)',
  'reports.monthly.tile.l1_profit': 'L1 利润 {amount}',
  'reports.monthly.detail.metric': '指标',
  'reports.monthly.detail.value': '数值',
  'reports.monthly.detail.period_start': '起始时间',
  'reports.monthly.detail.period_end': '结束时间',
  'reports.monthly.detail.paid_cny_net': '充值 (CNY 手续费后净额)',
  'reports.monthly.detail.usd_credited': '入账 USD',
  'reports.monthly.detail.ops_adjust_rows': '运营调整条数',
  'reports.monthly.detail.ops_adjust_usd': '运营调整 USD',
  'reports.monthly.detail.drift_cny': '差额 (已付 − 充值, CNY)',

  // ── Webhook events ──────────────────────────────────────────────
  'webhook_events.title': 'Webhook 事件',
  'webhook_events.subtitle':
    '各 provider 的 webhook 回调收件箱。支持筛选、展开原始负载、手动标记处理完成。',
  'webhook_events.filter.provider': '提供商',
  'webhook_events.filter.provider_placeholder': '例如 zpay',
  'webhook_events.filter.processed': '状态',
  'webhook_events.filter.processed.all': '全部',
  'webhook_events.filter.processed.pending': '待处理',
  'webhook_events.filter.processed.done': '已处理',
  'webhook_events.filter.since': '起始时间 (ISO-8601)',
  'webhook_events.loading': '正在加载 webhook 事件…',
  'webhook_events.error': '加载 webhook 事件失败。',
  'webhook_events.empty': '当前筛选条件下没有 webhook 事件。',
  'webhook_events.column.created_at': '创建时间',
  'webhook_events.column.provider': '提供商',
  'webhook_events.column.event_type': '事件类型',
  'webhook_events.column.status': '状态',
  'webhook_events.column.payload': '负载',
  'webhook_events.column.actions': '操作',
  'webhook_events.status.processed': '已处理',
  'webhook_events.status.pending': '待处理',
  'webhook_events.payload.expand': '展开负载',
  'webhook_events.action.mark_processed': '标记已处理',
  'webhook_events.action.marking': '正在标记…',
  'webhook_events.toast.marked': '事件已标记为已处理。',

  // ── Cron runs ───────────────────────────────────────────────────
  'cron_runs.title': '定时任务运行',
  'cron_runs.subtitle': 'control-loops 定时触发的运行历史。每行对应一次 tick 中所有到期处理器。',
  'cron_runs.filter.handler': '处理器',
  'cron_runs.filter.handler_placeholder': '例如 usage_rollup',
  'cron_runs.filter.since': '起始时间 (ISO-8601)',
  'cron_runs.filter.since_placeholder': 'YYYY-MM-DDTHH:MM:SSZ',
  'cron_runs.loading': '正在加载定时任务运行…',
  'cron_runs.error': '加载定时任务运行失败。',
  'cron_runs.empty': '当前筛选条件下无运行记录。',
  'cron_runs.column.scheduled_at': '计划时间',
  'cron_runs.column.duration': '耗时',
  'cron_runs.column.due': '到期',
  'cron_runs.column.ok': '成功',
  'cron_runs.column.fail': '失败',
  'cron_runs.column.outcomes': '结果',
  'cron_runs.outcomes.expand': '展开原始结果',

  // ── Ops users ───────────────────────────────────────────────────
  'ops_users.title': '运维账户',
  'ops_users.subtitle': '运维账户管理目录。禁用被盗账户、重新启用合法账户、强制重置密码。',
  'ops_users.loading': '正在加载运维账户…',
  'ops_users.error': '加载运维账户失败。',
  'ops_users.empty': '未找到运维账户。',
  'ops_users.column.email': '邮箱',
  'ops_users.column.roles': '角色',
  'ops_users.column.status': '状态',
  'ops_users.column.created_at': '创建时间',
  'ops_users.column.actions': '操作',
  'ops_users.status.active': '启用',
  'ops_users.status.disabled': '已禁用',
  'ops_users.status.no_password': '需重新设置',
  'ops_users.action.disable': '禁用',
  'ops_users.action.disabling': '禁用中…',
  'ops_users.action.enable': '重新启用',
  'ops_users.action.enabling': '启用中…',
  'ops_users.action.reset_password': '重置密码',
  'ops_users.action.resetting': '重置中…',
  'ops_users.action.reset_confirm': '确认重置',
  'ops_users.action.cancel': '取消',
  'ops_users.reset.confirm_prompt':
    '请输入邮箱 ({email}) 以确认。重置将删除凭证并使所有活跃会话失效。',
  'ops_users.toast.disabled': '已禁用运维账户。',
  'ops_users.toast.enabled': '已重新启用运维账户。',
  'ops_users.toast.reset': '密码已重置。已使 {sessions} 个会话失效。',

  // ── DR check ───────────────────────────────────────────────────
  'dr_check.title': '灾备就绪演练',
  'dr_check.subtitle': '对两个数据库运行按需检测。探测 platform-db 读写路径、gateway-db 读路径。',
  'dr_check.runner.title': '演练执行器',
  'dr_check.runner.body': '点击"运行演练"执行四探针序列，结果显示在下方。',
  'dr_check.action.run': '运行演练',
  'dr_check.action.running': '执行中…',
  'dr_check.idle': '暂无演练结果。点击"运行演练"开始。',
  'dr_check.toast.pass': '灾备演练：所有探针通过。',
  'dr_check.toast.fail': '灾备演练：一个或多个探针失败。',
  'dr_check.overall.pass_title': '所有探针通过。',
  'dr_check.overall.fail_title': '一个或多个探针失败。',
  'dr_check.overall.body': '开始于 {started}，耗时 {duration}ms。',
  'dr_check.column.probe': '探针',
  'dr_check.column.outcome': '结果',
  'dr_check.column.latency': '延迟',
  'dr_check.column.detail': '详情',
  'dr_check.probe.pass': '通过',
  'dr_check.probe.fail': '失败',

  // ── Toast ───────────────────────────────────────────────────────
  'toast.region_aria': '通知',
  'toast.dismiss_aria': '关闭通知',

  // ── Channels (#134, reworked #143) ─────────────────────────────
  'channels.title': '渠道管理',
  'channels.subtitle': '管理渠道和供应商',
  'channels.create_btn': '新建渠道',
  'channels.search.placeholder': '搜索渠道、slug、供应商…',
  'channels.empty': '暂无渠道',
  'channels.col.display_name': '渠道名称',
  'channels.col.rate_multiplier': '倍率',
  'channels.col.products': '产品',
  'channels.col.supplier': '供应商',
  'channels.col.official': '官方',
  'channels.col.created_at': '创建时间',
  'channels.row.delete': '删除渠道',
  'channels.create_modal.title': '新建渠道',
  'channels.create_modal.display_name': '显示名称',
  'channels.create_modal.display_name_placeholder': '输入渠道展示名称',
  'channels.create_modal.supplier_name': '供应商',
  'channels.create_modal.supplier_placeholder': '供应商名称',
  'channels.create_modal.slug_hint': '唯一标识',
  'channels.create_modal.rate_multiplier': '倍率',
  'channels.create_modal.rate_multiplier_hint': '租户计费 = 原厂价 × 倍率，默认 1.0',
  'channels.create_modal.is_official': '官方渠道',
  'channels.create_modal.is_official_hint': '启用后将标记为官方认证资源',
  'channels.create_modal.cancel': '取消',
  'channels.create_modal.submit': '创建',
  'channels.create_modal.creating': '创建中…',
  'channels.toast.created': '渠道创建成功',
  'channels.toast.deleted': '渠道已删除',
  'channels.toast.duplicate': '渠道标识已存在',

  // ── Channel Detail (#143) ─────────────────────────────────────
  'channel_detail.not_found': '渠道不存在',
  'channel_detail.vendors_count': '厂商',
  'channel_detail.models_count': '模型',
  'channel_detail.edit_info': '编辑信息',
  'channel_detail.add_model': '添加模型',
  'channel_detail.rate_multiplier': '渠道倍率',
  'channel_detail.rate_hint': '租户计费 = 原厂价 × 此倍率',
  'channel_detail.toast.rate_updated': '倍率已更新',
  'channel_detail.toast.info_updated': '渠道信息已更新',
  'channel_detail.empty': '暂无模型，点击"添加模型"配置渠道产品',
  'channel_detail.col.model': '模型',
  'channel_detail.edit_modal.title': '编辑渠道信息',
  'channel_detail.edit_modal.save': '保存',
  'channel_detail.edit_modal.saving': '保存中…',
  'channel_detail.edit_modal.display_name_required': '显示名称不能为空',
  'channel_detail.edit_modal.rate_invalid': '倍率必须是大于 0 的数字',
  'channel_detail.add_modal.title': '添加模型到渠道',
  'channel_detail.add_modal.vendor': '厂商',
  'channel_detail.add_modal.select_vendor': '选择厂商…',
  'channel_detail.add_modal.select_all': '全选',
  'channel_detail.add_modal.selected': '已选',
  'channel_detail.add_modal.no_models': '该厂商下没有可添加的模型（所有模型已在渠道中）',
  'channel_detail.add_modal.cancel': '取消',
  'channel_detail.add_modal.submit': '添加',
  'channel_detail.add_modal.adding': '添加中…',
  'channel_detail.toast.model_added': '模型已添加到渠道',
  'channel_detail.toast.models_added': '个模型已添加到渠道',
  'channel_detail.toast.model_removed': '模型已从渠道移除',

  // ── Model Mappings (#134) ──────────────────────────────────────
  'mappings.title': '模型映射',
  'mappings.legacy_title': '旧模型映射（迁移参考）',
  'mappings.legacy_subtitle':
    '这里只展示已存在的旧 mapping 记录，表示历史上这个渠道 SKU 曾手工指向某个上游模型名；它不是新的热路径配置。',
  'mappings.legacy.empty': '暂无旧模型映射。',
  'mappings.legacy.col.status': '旧状态',
  'mappings.legacy.status.mapped': '旧映射存在',
  'mappings.filter.all_vendors': '全部厂商',
  'mappings.filter.all_channels': '全部渠道',
  'mappings.empty': '暂无模型映射',
  'mappings.col.vendor': '厂商',
  'mappings.col.channel': '渠道',
  'mappings.col.model': '模型',
  'mappings.col.upstream_name': '上游模型名',
  'mappings.add_modal.title': '添加模型映射',
  'mappings.add_modal.select_vendor': '选择厂商…',
  'mappings.add_modal.select_channel': '选择渠道…',
  'mappings.add_modal.select_model': '选择模型…',
  'mappings.add_modal.select_protocol': '选择协议…',
  'mappings.add_modal.cancel': '取消',
  'mappings.add_modal.submit': '添加',
  'mappings.add_modal.adding': '添加中…',
  'mappings.toast.created': '模型映射已添加',
  'mappings.toast.deleted': '模型映射已删除',
  'mappings.toast.duplicate': '该映射已存在',

  // ── Model Detail — Channel Pricing (#134) ──────────────────────
  'model_detail.channel_pricing.title': '渠道售价',
  'model_detail.channel_pricing.empty': '该厂商暂无渠道',
  'model_detail.channel_pricing.col_channel': '渠道',
  'model_detail.channel_pricing.col_multiplier': '倍率',
  'model_detail.channel_pricing.col_input_price': '输入价 $/M',
  'model_detail.channel_pricing.col_output_price': '输出价 $/M',
  'model_detail.channel_pricing.col_supplier': '供应商',

  // ── Vendors (#143) ────────────────────────────────────────────
  'shell.nav.vendors': '厂商',
  'vendors.title': '厂商管理',
  'vendors.subtitle': '管理平台支持的模型厂商',
  'vendors.create_btn': '新建厂商',
  'vendors.search.placeholder': '搜索厂商…',
  'vendors.empty': '暂无厂商',
  'vendors.col.display_name': '厂商名称',
  'vendors.col.created_at': '创建时间',
  'vendors.row.delete': '删除厂商',
  'vendors.create_modal.title': '新建厂商',
  'vendors.create_modal.display_name': '显示名称',
  'vendors.create_modal.display_name_placeholder': '如 Anthropic',
  'vendors.create_modal.cancel': '取消',
  'vendors.create_modal.submit': '创建',
  'vendors.create_modal.creating': '创建中…',
  'vendors.toast.created': '厂商创建成功',
  'vendors.toast.deleted': '厂商已删除',
  'vendors.toast.duplicate': '厂商标识已存在',

  // ── Upstream Accounts (upstream detail accounts tab) ────────────
  'upstream_detail.tab.accounts': '账户',
  'upstream_accounts.title': '供应商账户',
  'upstream_accounts.add_btn': '新建账户',
  'upstream_accounts.empty': '暂无账户',
  'upstream_accounts.count': '共 {count} 个账户',
  'upstream_accounts.add_modal.title': '新建供应商账户',
  'upstream_accounts.add_modal.label': '账户标签',
  'upstream_accounts.add_modal.label.placeholder': '如: hellsoul86@gmail.com',
  'upstream_accounts.add_modal.rpm': 'RPM 限制',
  'upstream_accounts.add_modal.rate_multiplier': '账户折扣倍率',
  'upstream_accounts.add_modal.submit': '创建',
  'upstream_accounts.add_modal.creating': '创建中…',
  'upstream_accounts.add_modal.cancel': '取消',
  'upstream_accounts.add_modal.error.label_required': '账户标签不能为空',
  'upstream_accounts.toast.created': '账户已创建',
  'upstream_accounts.toast.deleted': '账户已删除',
  'upstream_accounts.section.keys': 'API 密钥',
  'upstream_accounts.channels.none': '未绑定渠道（匹配所有）',
  'upstream_accounts.keys.none': '该账户下暂无密钥',
  'upstream_accounts.keys.add_btn': '添加密钥',
  'upstream_accounts.keys.add_modal.title': '添加 API 密钥',
  'upstream_accounts.keys.add_modal.auth_secret_ref': '密钥值',
  'upstream_accounts.keys.add_modal.auth_secret_ref.placeholder': 'sk-...',
  'upstream_accounts.keys.add_modal.label': '标签（可选）',
  'upstream_accounts.keys.add_modal.rpm': 'RPM 限制',
  'upstream_accounts.keys.add_modal.submit': '创建',
  'upstream_accounts.keys.add_modal.creating': '创建中…',
  'upstream_accounts.keys.add_modal.channels': '关联渠道',
  'upstream_accounts.keys.add_modal.channels_hint': '不选则匹配所有渠道',
  'upstream_accounts.keys.add_modal.error.secret_required': '密钥值不能为空',
  'upstream_accounts.keys.toast.created': '密钥已创建',
  'upstream_accounts.keys.toast.channels_failed': '渠道绑定失败，请手动设置',
  'upstream_accounts.keys.toast.deleted': '密钥已删除',
  'upstream_accounts.keys.toast.channels_updated': '渠道绑定已更新',
  'upstream_accounts.keys.edit_channels': '编辑渠道绑定',
  'upstream_accounts.chip.enabled': '启用',
  'upstream_accounts.chip.disabled': '禁用',
  'upstream_accounts.chip.status_label': '状态',
  'upstream_accounts.rate_multiplier_short': '折扣倍率',

  'upstream_detail.channel_costs.title': '上游渠道成本倍率',
  'upstream_detail.channel_costs.subtitle':
    '缺省等于下游销售渠道倍率，人工调整后会用于 L2 成本和历史重算。',
  'upstream_detail.channel_costs.empty': '暂无渠道成本倍率。',
  'upstream_detail.channel_costs.col.channel': '渠道',
  'upstream_detail.channel_costs.col.vendor': '厂商/协议',
  'upstream_detail.channel_costs.col.multiplier': '成本倍率',
  'upstream_detail.channel_costs.multiplier_label': '成本倍率',
  'upstream_detail.channel_costs.invalid': '成本倍率必须是大于 0 的数字',
  'upstream_detail.channel_costs.updated': '上游渠道成本倍率已更新',

  // ── Key Buckets (#143) ────────────────────────────────────────
  'key_buckets.title': 'API 密钥桶',
  'key_buckets.add_btn': '添加密钥',
  'key_buckets.empty': '暂无密钥桶',
  'key_buckets.col.label': '标签',
  'key_buckets.col.key_preview': '密钥预览',
  'key_buckets.col.status': '状态',
  'key_buckets.enabled': '启用',
  'key_buckets.disabled': '停用',
  'key_buckets.add_modal.title': '添加 API 密钥桶',
  'key_buckets.add_modal.label': '标签',
  'key_buckets.add_modal.label_placeholder': '如 生产密钥 #1',
  'key_buckets.add_modal.cancel': '取消',
  'key_buckets.add_modal.submit': '添加',
  'key_buckets.add_modal.creating': '添加中…',
  'key_buckets.toast.created': '密钥桶添加成功',
  'key_buckets.toast.deleted': '密钥桶已删除',
  'key_buckets.confirm_delete': '确认删除',
} as const;
